import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../state/auth';
import type { Item, Project } from '../../lib/types';
import { Icon } from '../../components/Icon';
import { CopyButton } from '../../components/ui';
import { useToast } from '../../components/toast';

type ExportStatus = 'unresolved' | 'all' | 'resolved';

/** Everything needed to hand this project's feedback to Claude Code, Codex or a script. */
export function HandoffTab({ project, items }: { project: Project; items: Item[] }) {
  const { meta, isStaff } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState<ExportStatus>('unresolved');
  const [itemId, setItemId] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const query = `status=${status}${itemId ? `&item=${itemId}` : ''}`;
  const appUrl = meta?.appUrl ?? location.origin;
  const apiBase = `${appUrl}/api/v1/projects/${project.slug}`;

  const load = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/projects/${project.id}/export.md?${query}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Export failed (${res.status}).`);
      setPreview(await res.text());
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const curl = `curl -H "Authorization: Bearer $REVISIONS_API_KEY" \\\n  "${apiBase}/export.md?status=unresolved" -o FEEDBACK.md`;
  const mcp = `claude mcp add --transport http revisions ${appUrl}/mcp \\\n  --header "Authorization: Bearer $REVISIONS_API_KEY"`;
  const prompt = `Read FEEDBACK.md. For each open item, find the element or asset it points to, make the change, then mark it resolved through the Revisions API with a one-line note of what you changed.`;

  return (
    <div className="stack" style={{ gap: 24, maxWidth: 820 }}>
      <div className="card card-pad stack" style={{ gap: 16 }}>
        <div>
          <h3 className="card-title">FEEDBACK.md</h3>
          <p className="muted">
            One file with every revision and exactly where it points: page, CSS selector and element HTML for websites; x/y position for images; timecode for video. Drop it into a repo and ask Claude Code or Codex to work through it.
          </p>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <select className="select" style={{ width: 'auto' }} aria-label="Which feedback" value={status} onChange={(event) => (setStatus(event.target.value as ExportStatus), setPreview(null))}>
            <option value="unresolved">Open and in progress</option>
            <option value="all">Everything</option>
            <option value="resolved">Resolved only</option>
          </select>
          <select className="select" style={{ width: 'auto', maxWidth: 260 }} aria-label="Which item" value={itemId} onChange={(event) => (setItemId(event.target.value), setPreview(null))}>
            <option value="">All items and general feedback</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <a className="btn btn-primary" href={`/api/projects/${project.id}/export.md?${query}&download=1`}>
            <Icon name="download" /> Download FEEDBACK.md
          </a>
          <a className="btn btn-secondary" href={`/api/projects/${project.id}/export.json?${query}&download=1`}>
            <Icon name="download" /> JSON
          </a>
          <button className="btn btn-secondary" onClick={load} disabled={busy}>
            {busy ? <span className="spinner" /> : <Icon name="eye" />} Preview
          </button>
        </div>
        {preview !== null && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="row-between">
              <span className="muted">{preview.split('\n').length} lines</span>
              <CopyButton text={preview} label="Copy Markdown" />
            </div>
            <pre className="codeblock" style={{ maxHeight: 420, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {preview}
            </pre>
          </div>
        )}
      </div>

      <div className="card card-pad stack" style={{ gap: 16 }}>
        <div>
          <h3 className="card-title">Live access for agents and scripts</h3>
          <p className="muted">
            The same data is available over a REST API and an MCP server, so an agent can read fresh feedback, reply, resolve revisions and upload revised images or videos.{' '}
            {isStaff ? (
              <>
                Create a key under <Link to="/settings/api-keys">API keys</Link>.
              </>
            ) : (
              'Ask MK-Way for an API key.'
            )}
          </p>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-between">
            <span className="field-label">Fetch the brief</span>
            <CopyButton text={curl} />
          </div>
          <pre className="codeblock">{curl}</pre>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-between">
            <span className="field-label">Connect Claude Code over MCP</span>
            <CopyButton text={mcp} />
          </div>
          <pre className="codeblock">{mcp}</pre>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-between">
            <span className="field-label">Prompt to start with</span>
            <CopyButton text={prompt} />
          </div>
          <pre className="codeblock" style={{ whiteSpace: 'pre-wrap' }}>
            {prompt}
          </pre>
        </div>
        <dl className="kv">
          <dt>Revisions (JSON)</dt>
          <dd>
            <code className="truncate" style={{ display: 'block' }}>{`GET ${apiBase}/comments?status=unresolved`}</code>
          </dd>
          <dt>Resolve with a note</dt>
          <dd>
            <code className="truncate" style={{ display: 'block' }}>{`PATCH ${appUrl}/api/v1/comments/{id}  {"status":"resolved","reply":"…"}`}</code>
          </dd>
          <dt>New version</dt>
          <dd>
            <code className="truncate" style={{ display: 'block' }}>{`POST ${appUrl}/api/v1/items/{id}/versions  (multipart: file)`}</code>
          </dd>
          <dt>Reference</dt>
          <dd>
            <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">
              OpenAPI description <Icon name="external" size={12} />
            </a>
          </dd>
        </dl>
      </div>
    </div>
  );
}
